import React from 'react';
import styled from 'styled-components';
import { useVolunteerRequests } from '../hooks/useVolunteerRequests';
import { useWebsiteVolunteerRequests } from '../hooks/useWebsiteVolunteerRequests';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const SectionTitle = styled.h2`
  margin: 1.5rem 0 0.75rem;
  font-size: 1.25rem;
  color: #334155;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const MessageCell = styled.td`
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VolunteerRequestsPage: React.FC = () => {
  const { data, isLoading, error } = useVolunteerRequests();
  const { data: websiteData, isLoading: websiteLoading, error: websiteError } = useWebsiteVolunteerRequests();

  const hasApp = (data?.length ?? 0) > 0;
  const hasWebsite = (websiteData?.length ?? 0) > 0;
  const loading = isLoading || websiteLoading;
  const anyError = error || websiteError;

  return (
    <Page>
      <HeaderTitle>Volunteer Requests</HeaderTitle>
      <HelperText>
        Requests from the mobile app and from the public website &quot;Join A Ministry Today&quot; form.
      </HelperText>
      {loading && <HelperText>Loading…</HelperText>}
      {anyError && <HelperText style={{ color: 'var(--error)' }}>Some data could not be loaded. You may still see partial results.</HelperText>}

      {!isLoading && hasApp && (
        <>
          <SectionTitle>From app</SectionTitle>
          <TableCard>
            <TableWrap>
              <Table>
                <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Opportunity</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((request) => (
                  <tr key={request.id}>
                    <td>{request.name ?? '—'}</td>
                    <td>{request.phone ?? '—'}</td>
                    <td>{request.volunteer_opportunities?.title ?? '—'}</td>
                    <td>
                      {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              </Table>
            </TableWrap>
          </TableCard>
        </>
      )}

      {!websiteLoading && hasWebsite && (
        <>
          <SectionTitle>From website (Join A Ministry)</SectionTitle>
          <TableCard>
            <TableWrap>
              <Table>
                <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {websiteData?.map((request) => (
                  <tr key={request.id}>
                    <td>{request.name ?? '—'}</td>
                    <td>{request.email ?? '—'}</td>
                    <td>{request.subject ?? '—'}</td>
                    <MessageCell title={request.message ?? undefined}>
                      {request.message ?? '—'}
                    </MessageCell>
                    <td>
                      {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </TableWrap>
          </TableCard>
        </>
      )}

      {!loading && !hasApp && !hasWebsite && (
        <HelperText>No volunteer requests yet.</HelperText>
      )}
    </Page>
  );
};

export default VolunteerRequestsPage;
